'use client';

import { useState, useEffect } from 'react';
import { Button } from './Button';
import { X, Cookie, Sparkles } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { setCookieConsent, getCookieConsent } from '@/lib/cookieConsent';

export function CookieBanner() {
  const t = useTranslations('cookies.banner');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = getCookieConsent();
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    // Set consent cookie (essential cookie, always created)
    setCookieConsent('accepted');
    setIsVisible(false);
    // Reload to apply cookie settings
    window.location.reload();
  };

  const handleDecline = () => {
    // Set consent cookie (essential cookie, always created)
    setCookieConsent('declined');
    setIsVisible(false);
    // Reload to apply cookie settings
    window.location.reload();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="relative bg-gradient-to-r from-pink-white via-white to-pink-white border-2 border-accent/40 rounded-2xl shadow-2xl overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-accent/10 rounded-full blur-3xl -mr-16 -mt-16" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary/10 rounded-full blur-2xl -ml-12 -mb-12" />
          
          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-4 p-6">
            {/* Cookie icon with animation */}
            <div className="flex-shrink-0 relative">
              <div className="relative">
                <div className="absolute inset-0 bg-accent/20 rounded-full blur-lg animate-pulse" />
                <Cookie className="w-10 h-10 text-accent relative z-10 animate-bounce-slow" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-bold text-foreground">
                  {t('title')}
                </h3>
                <Sparkles className="w-4 h-4 text-accent animate-pulse" />
              </div>
              <p className="text-primary/85 text-sm md:text-base leading-relaxed mb-2">
                {t('message')}
              </p>
              <Link 
                href="/politique-confidentialite" 
                className="inline-flex items-center text-accent hover:text-primary transition-colors font-medium text-sm group"
              >
                {t('link')}
                <span className="ml-1 group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-3 w-full md:w-auto flex-shrink-0">
              <Button
                onClick={handleAccept}
                size="sm"
                className="whitespace-nowrap shadow-md hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-r from-accent to-secondary text-white border border-accent/30"
              >
                {t('accept')}
              </Button>
              <Button
                onClick={handleDecline}
                size="sm"
                className="whitespace-nowrap shadow-md hover:shadow-lg transition-all hover:scale-105"
              >
                {t('decline')}
              </Button>
              <button
                onClick={handleDecline}
                className="text-primary hover:text-foreground hover:bg-primary/10 rounded-full p-2 transition-all"
                aria-label={t('close')}
                title={t('close')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

